import { Link } from "@tanstack/react-router";
import { Home } from "lucide-react";
import { Fragment, type ReactNode } from "react";
import { cn } from "@/lib/utils";

type Crumb = {
	label: ReactNode;
	to?: string;
	search?: Record<string, unknown>;
};

/**
 * Breadcrumb trail for public pages. Always prepends a Home link; pass the
 * intermediate sections and the current page via `items` (the last item is
 * rendered as the current page and should omit `to`).
 */
export function PageBreadcrumb({
	items,
	trailing,
	className,
}: {
	items: Crumb[];
	/** Optional content pinned to the right (e.g. a result count). */
	trailing?: ReactNode;
	className?: string;
}) {
	const lastIndex = items.length - 1;

	return (
		<div
			className={cn(
				"flex items-center justify-between gap-3 font-mono text-xs uppercase tracking-[0.14em]",
				className,
			)}
		>
			<nav
				aria-label="Breadcrumb"
				className="flex min-w-0 items-center text-muted-foreground"
			>
				<Link
					to="/"
					search={{ unsubscribed: undefined }}
					aria-label="Home"
					className="flex size-7 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-brand"
				>
					<Home className="size-3.5" aria-hidden="true" />
				</Link>
				{items.map((item, index) => {
					const isCurrent = index === lastIndex;
					return (
						<Fragment key={item.to ?? "current"}>
							<Separator />
							{isCurrent || !item.to ? (
								<span
									aria-current={isCurrent ? "page" : undefined}
									className={cn(
										"truncate px-1.5 py-1",
										isCurrent
											? "font-medium text-foreground"
											: "text-muted-foreground",
									)}
								>
									{item.label}
								</span>
							) : (
								<Link
									to={item.to}
									search={item.search}
									className="shrink-0 rounded-md px-1.5 py-1 transition-colors hover:bg-muted hover:text-brand"
								>
									{item.label}
								</Link>
							)}
						</Fragment>
					);
				})}
			</nav>
			{trailing ? <div className="shrink-0">{trailing}</div> : null}
		</div>
	);
}

function Separator() {
	return (
		<span
			aria-hidden="true"
			className="px-0.5 text-muted-foreground/40 select-none"
		>
			/
		</span>
	);
}
